import { topics } from '@tmap/mmm-core/contributionTopics';
import FeatureVibrant, { iconsLibrary } from '../../components/communityEditor/blocks/featureVibrant';
import FeatureCollage from '../../components/communityEditor/blocks/featureCollage';
import TextBoldHighlight from '../../components/communityEditor/blocks/textBoldHighlight';
import FeatureVideo from '../../components/communityEditor/blocks/featureVideo';
import previewContributions from './previewContributions';

export const MEDIA_TYPES = {
  VIDEO: 'video',
  IMAGE: 'image',
};

export const blocksConfig = [
  {
    title: 'Feature',
    type: 'feature',
    children: [
      {
        variant: 'vibrant',
        variantTitle: 'Vibrant',
        fields: [
          {
            name: 'icon',
            type: 'array',
            title: 'Icon',
            subtitle: 'Optional',
            default: 'none',
            options: iconsLibrary,
          },
          {
            name: 'heading',
            type: 'string',
            title: 'Block Heading',
            subtitle: '(80 characters max)',
            placeholder: 'Write a section title',
            validation: (value) => value.length <= 80,
          },
        ],
        contentLimit: 5,
        Component: FeatureVibrant,
        isValid: ({ content }) => content?.length > 0,
        componentPreviewProps: {
          heading: 'Your Community Spotlight',
          icon: 'trophy',
          content: previewContributions.slice(3, 6),
        },
      },
      {
        variant: 'collage',
        variantTitle: 'Collage',
        fields: [
          {
            name: 'overline',
            type: 'string',
            title: 'Block Overline',
            subtitle: '(80 characters max)',
            placeholder: 'Write a section title',
            validation: (value) => value.length <= 80,
          },
          {
            name: 'heading',
            type: 'string',
            title: 'Block Heading',
            subtitle: '(80 characters max)',
            placeholder: 'Write a short catchy description',
            validation: (value) => value.length <= 80,
          },
          {
            name: 'topic',
            type: 'array',
            title: 'See More Topic Page',
            subtitle: 'Optional',
            default: 'none',
            options: [
              { value: 'none', label: 'None (Jump to feed)' },
              ...Object.keys(topics).map((key) => ({ label: topics[key].plural, value: key })),
            ],
            dynamicOptionsType: 'popularTags',
          },
        ],
        contentLimit: 7,
        contentMediaTypes: [MEDIA_TYPES.IMAGE, MEDIA_TYPES.VIDEO],
        Component: FeatureCollage,
        componentPreviewProps: {
          overline: 'Community Highlights',
          heading: 'Do weekends like a Local.',
          content: previewContributions.slice(1),
        },
        blockHideBorder: true,
        isValid: ({ content }) => content?.some((item) => item?.contribution?.media?.length > 0),
      },
      {
        variant: 'video',
        variantTitle: 'Video',
        fields: [
          {
            name: 'overline',
            type: 'string',
            title: 'Block Overline',
            subtitle: '(40 characters max)',
            placeholder: 'Write a section title',
            validation: (value) => value.length <= 40,
          },
          {
            name: 'heading',
            type: 'string',
            title: 'Block Heading',
            subtitle: '(80 characters max)',
            placeholder: 'Write a short catchy description',
            validation: (value) => value.length <= 80,
          },
          {
            name: 'color',
            type: 'array',
            title: 'Theme Color',
            default: 'pink',
            options: [
              { value: 'pink', label: 'Pink' },
              { value: 'green', label: 'Green' },
              { value: 'aqua', label: 'Aqua' },
            ],
          },
        ],
        contentLimit: 1,
        contentMediaTypes: [MEDIA_TYPES.VIDEO],
        Component: FeatureVideo,
        componentPreviewProps: {
          overline: 'Big Video',
          heading: 'Check out this heartwarming story!',
          color: 'pink',
          content: [{
            contribution: {
              ...previewContributions[0].contribution,
              media: [{
                ...previewContributions[0].contribution.media[0],
                type: 'video',
                thumbnailUrl: previewContributions[0].contribution.media[0].url,
              }],
            },
          }],
        },
        blockColor: 'dark',
        blockSpacing: 'medium',
        isValid: ({ content }) => content?.[0]?.contribution?.media?.some((m) => m.type === 'video' || m.type === 'videoEmbed'),
      },
    ],
  },
  {
    title: 'Text',
    type: 'text',
    children: [
      {
        variant: 'boldHighlight',
        variantTitle: 'Bold Highlight',
        fields: [
          {
            name: 'overline',
            type: 'string',
            title: 'Overline',
            subtitle: '(40 characters max)',
            placeholder: 'Text that adds context',
            validation: (value) => value.length <= 40,
          },
          {
            name: 'heading',
            type: 'text',
            title: 'Bold Text',
            subtitle: '(175 characters max)',
            placeholder: 'Some eyecatching messaging',
            validation: (value) => value.length <= 175,
          },
        ],
        Component: TextBoldHighlight,
        componentPreviewProps: { overline: 'Introduction to your concept', heading: 'Eyecatching Messaging to Let Movers\nKnow What You\'re All About' },
        isValid: ({ overline, heading }) => Boolean(overline || heading),
      },
    ],
  },
];

export const hydrateBlock = (data) => {
  const { type, variant } = data;
  const typeTemplate = blocksConfig.find((group) => group.type === type);
  const variantTemplate = typeTemplate.children.find((child) => child.variant === variant);
  const hydratedBlock = {
    ...variantTemplate,
    type,
    data,
    typeTitle: typeTemplate.title,
  };
  return hydratedBlock;
};

export const dehydrateBlock = (hydratedBlock) => {
  const allowedKeys = ['type', 'contributionId', 'topic'];
  const dehydrate = (content) => Object.fromEntries(
    Object.entries(content).filter(([key]) => allowedKeys.includes(key)),
  );
  const dehydratedContent = hydratedBlock.data.content && hydratedBlock.data.content.map(dehydrate);
  return {
    ...hydratedBlock.data,
    content: dehydratedContent,
  };
};
